/**
 * In-memory API adapter.
 *
 * Every function here mirrors one endpoint, and `src/api/http/adapter.ts`
 * implements the same signatures over the Portal API. `src/api/index.ts` picks
 * one at boot; nothing in src/api/queries or the pages knows which.
 *
 * Tenancy note: every read filters on clientId even though the mock store is
 * local. That is deliberate — it keeps the call sites honest about scoping so
 * the swap to a scoped HTTP client is mechanical.
 */
import type {
  Client,
  ClientWithPending,
  CopyDoc,
  CrawledPage,
  CrawlRun,
  EntityType,
  FacultyMember,
  FacultyMemberInput,
  MarkKind,
  MediaDetail,
  MediaItem,
  NewClient,
  PartnerFact,
  Program,
  ProgramDetail,
  ProgramPatch,
  ProgramRef,
  ProgramType,
  Quote,
  QuoteDetail,
  Reviewable,
  ReviewStatus,
  ReviewSummary,
  Segment,
  SegmentPatch,
  SlateProgram,
  Content,
  ContentOwner,
  ContentPatch,
  ContentSummaries,
  ProgramContent,
  WbProgram,
  WbProgramPatch,
  WbProgramTarget,
  SuggestedProgram,
  User,
} from '@/types'
import { ApiError } from '@/api/errors'
import * as seed from './seed'

const LATENCY = 220

const db = {
  clients: [...seed.clients],
  programTypes: [...seed.programTypes],
  segments: [...seed.segments],
  faculty: [...seed.faculty],
  programs: [...seed.programs],
  wbPrograms: [...seed.wbPrograms],
  content: [] as StoredContent[],
  slatePrograms: [...seed.slatePrograms],
  quotes: [...seed.quotes],
  media: [...seed.media],
  facts: [...seed.facts],
  copyDocs: [...seed.copyDocs],
  crawlRuns: [...seed.crawlRuns],
  pages: [...seed.pages],
}

function delay<T>(value: T, ms = LATENCY): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms))
}

function uid(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`
}

function hash() {
  return Math.random().toString(16).slice(2, 18)
}

const CURRENT_USER: User = { name: 'Joe', email: 'joe@waybettermarketing.com' }

/* ------------------------------------------------------------------ auth */

export async function getSession(): Promise<User | null> {
  const active = sessionStorage.getItem('wbm.session')
  return delay(active ? CURRENT_USER : null, 80)
}

export async function signIn(): Promise<User> {
  sessionStorage.setItem('wbm.session', '1')
  return delay(CURRENT_USER, 400)
}

export async function signOut(): Promise<void> {
  sessionStorage.removeItem('wbm.session')
  return delay(undefined, 80)
}

/* --------------------------------------------------------------- clients */

export async function listClients(): Promise<ClientWithPending[]> {
  const withCounts = db.clients.map((c) => ({
    ...c,
    pendingCount:
      countPending(db.programs, c.id) +
      countPending(db.quotes, c.id) +
      countPending(db.media, c.id) +
      countPending(db.facts, c.id),
  }))
  return delay(withCounts)
}

export async function getClient(clientId: string): Promise<Client> {
  const found = db.clients.find((c) => c.id === clientId)
  if (!found) throw new ApiError(404, 'Client not found')
  return delay(found)
}

export async function createClient(input: NewClient): Promise<Client> {
  const id = String(input.id)
  if (db.clients.some((c) => c.id === id)) throw new ApiError(409, `client ${id} already exists`)
  const now = new Date().toISOString()
  const client: Client = {
    ...input,
    id,
    includePatterns: [],
    depthLimit: null,
    pipelineStage: null,
    lastCrawlAt: null,
    lastCrawlRunId: null,
    createdAt: now,
    updatedAt: now,
  }
  db.clients.push(client)
  // As the API: every client starts with "Undecided", a segment, a WB program and a program (PC-31, PC-33).
  const undecided = await createSegment(id, 'Undecided')
  const wb = await createWbProgram(id, { name: 'Undecided', segmentId: undecided.id })
  await upsertProgram(id, { name: 'Undecided', wbProgramId: wb.id })
  return delay(client, 350)
}

export async function updateClient(clientId: string, patch: Partial<Client>): Promise<Client> {
  const i = db.clients.findIndex((c) => c.id === clientId)
  if (i === -1) throw new ApiError(404, 'Client not found')
  const next = { ...db.clients[i]!, ...patch, updatedAt: new Date().toISOString() }
  db.clients[i] = next
  return delay(next, 350)
}

function countPending(rows: Array<{ clientId: string; status: ReviewStatus }>, clientId: string) {
  return rows.filter((r) => r.clientId === clientId && r.status === 'pending').length
}

export async function getReviewSummary(clientId: string): Promise<ReviewSummary> {
  return delay({
    programs: countPending(db.programs, clientId),
    quotes: countPending(db.quotes, clientId),
    media: countPending(db.media, clientId),
    facts: countPending(db.facts, clientId),
  })
}

export async function listClientTags(clientId: string): Promise<string[]> {
  const all = [
    ...db.programs.filter((r) => r.clientId === clientId).flatMap((r) => r.tags),
    ...db.quotes.filter((r) => r.clientId === clientId).flatMap((r) => r.tags),
    ...db.media.filter((r) => r.clientId === clientId).flatMap((r) => r.tags),
    ...db.facts.filter((r) => r.clientId === clientId).flatMap((r) => r.tags),
    ...db.copyDocs.filter((r) => r.clientId === clientId).flatMap((r) => r.tags),
  ]
  return delay([...new Set(all)].sort(), 60)
}

/* ----------------------------------------------------------------- crawl */

export async function getLatestCrawlRun(clientId: string): Promise<CrawlRun | null> {
  const runs = db.crawlRuns.filter((r) => r.clientId === clientId)
  return delay(runs[runs.length - 1] ?? null)
}

export async function listPages(clientId: string): Promise<CrawledPage[]> {
  return delay(db.pages.filter((p) => p.clientId === clientId))
}

export async function excludePage(pageId: string): Promise<CrawledPage> {
  const p = db.pages.find((x) => x.id === pageId)
  if (!p) throw new ApiError(404, 'Page not found')
  p.excluded = true
  return delay(p, 250)
}

export async function setPageImportance(pageId: string, importance: number): Promise<CrawledPage> {
  const p = db.pages.find((x) => x.id === pageId)
  if (!p) throw new ApiError(404, 'Page not found')
  p.importance = importance
  return delay(p, 200)
}

export async function retryPage(pageId: string): Promise<CrawledPage> {
  const p = db.pages.find((x) => x.id === pageId)
  if (!p) throw new ApiError(404, 'Page not found')
  p.error = null
  return delay(p, 400)
}

export async function startCrawl(clientId: string): Promise<CrawlRun> {
  // The API's two refusals (APP-54), so the mock exercises the same error path.
  const client = db.clients.find((c) => c.id === clientId)
  if (!client) throw new ApiError(404, 'Client not found')
  if (client.pageLimit === null) {
    throw new ApiError(422, 'client has no page limit; set one before starting a crawl')
  }
  if (db.crawlRuns.some((r) => r.clientId === clientId && r.status === 'running')) {
    throw new ApiError(409, 'a crawl run is already in progress for this client')
  }
  const run: CrawlRun = {
    id: uid('run'),
    clientId,
    startedAt: new Date().toISOString(),
    finishedAt: null,
    status: 'running',
    found: 0,
    crawled: 0,
    failed: 0,
    skipped: 0,
  }
  db.crawlRuns.push(run)
  return delay(run, 500)
}

/* --------------------------------------------------------- program types */

/** Global lookup, so no clientId — the one read here that is not tenant-scoped. */
export async function listProgramTypes(): Promise<ProgramType[]> {
  return delay(db.programTypes)
}

/* -------------------------------------------------------------- segments */

/**
 * As the API: the featured quote is shown only while it is approved, the image only while it is
 * an approved, usable image; the faculty member always.
 */
function withDetails(s: Segment): Segment {
  const q = db.quotes.find(
    (x) => x.id === s.featuredQuoteId && x.clientId === s.clientId && x.status === 'approved',
  )
  const f = db.faculty.find((x) => x.id === s.facultyMemberId && x.clientId === s.clientId)
  const m = db.media.find(
    (x) =>
      x.id === s.imageAssetId &&
      x.clientId === s.clientId &&
      x.kind === 'image' &&
      x.status === 'approved' &&
      x.usable,
  )
  return {
    ...s,
    featuredQuote: q
      ? {
          id: q.id,
          text: q.text,
          studentName: q.speakerName,
          classYear: q.classYear,
          majors: q.majors,
          studentImageUrl: q.studentImageUrl,
        }
      : null,
    facultyMember: f
      ? { id: f.id, name: f.name, email: f.email, imageUrl: f.imageUrl, active: f.active }
      : null,
    imageUrl: m?.url ?? null,
  }
}

export async function listSegments(clientId: string): Promise<Segment[]> {
  return delay(
    db.segments
      .filter((s) => s.clientId === clientId)
      .sort((a, b) => a.position - b.position)
      .map(withDetails),
  )
}

/** The Portal API serves the list from `common/segments.py`; a copy here for the mock. */
const DEFAULT_SEGMENTS = [
  'Art',
  'Fine & Performing Arts',
  'Business',
  'Communication',
  'Education',
  'Health Sciences',
  'Humanities',
  'Nursing',
  'Social Sciences',
  'Sciences',
  'Natural Sciences',
  'Math & Computer Science',
  'Math & Technology',
  'Performing Arts',
  'Pre-Professional',
  'Undecided',
]

export async function listDefaultSegments(_clientId: string): Promise<string[]> {
  return delay([...DEFAULT_SEGMENTS], 0)
}

/** The Portal API serves the list from `common/suggested_programs.py`; a copy here for the mock. */
const SUGGESTED_PROGRAMS: SuggestedProgram[] = [
  { name: 'Pre-Med', variations: ['Pre-Medicine', 'Pre-Medical', 'Pre-Medical Studies'] },
  { name: 'Pre-Law', variations: ['Pre-Law Studies'] },
  { name: 'Pre-Vet', variations: ['Pre-Veterinary', 'Pre-Veterinary Medicine', 'Pre-Veterinary Studies'] },
  {
    name: 'Pre-Health Professions Programs',
    variations: ['Pre-Health', 'Pre-Health Professions', 'Pre-Health Professional Programs'],
  },
  { name: 'Pre-Nursing', variations: [] },
  { name: 'Pre-Pharmacy', variations: ['Pre-Pharmacy Studies'] },
]

export async function listSuggestedPrograms(_clientId: string): Promise<SuggestedProgram[]> {
  return delay(SUGGESTED_PROGRAMS.map((s) => ({ ...s, variations: [...s.variations] })), 0)
}

/** As the API: names are unique per client, ignoring case. */
function assertNameFree(clientId: string, name: string, self?: string) {
  const key = name.trim().toLowerCase()
  if (db.segments.some((s) => s.clientId === clientId && s.id !== self && s.name.toLowerCase() === key)) {
    throw new ApiError(409, `A segment named ${name} already exists`)
  }
}

function findSegment(clientId: string, segmentId: string): Segment {
  const s = db.segments.find((x) => x.id === segmentId && x.clientId === clientId)
  if (!s) throw new ApiError(404, 'Segment not found')
  return s
}

export async function createSegment(clientId: string, name: string): Promise<Segment> {
  assertNameFree(clientId, name)
  const existing = db.segments.filter((s) => s.clientId === clientId)
  const position = existing.reduce((max, s) => Math.max(max, s.position + 1), 0)
  const seg: Segment = {
    id: uid('sg'),
    clientId,
    name,
    position,
    headline: null,
    description: null,
    featuredQuoteId: null,
    featuredQuote: null,
    facultyMemberId: null,
    facultyMember: null,
    imageAssetId: null,
    imageUrl: null,
  }
  db.segments.push(seg)
  return delay(withDetails(seg), 250)
}

export async function renameSegment(clientId: string, segmentId: string, name: string): Promise<Segment> {
  return updateSegment(clientId, segmentId, { name })
}

/**
 * As the API: a new pick must be an approved quote, an active faculty member or an approved,
 * usable image; the current one stands.
 */
export async function updateSegment(
  clientId: string,
  segmentId: string,
  patch: SegmentPatch,
): Promise<Segment> {
  const s = findSegment(clientId, segmentId)
  if (patch.name !== undefined) assertNameFree(clientId, patch.name, segmentId)
  const quoteId = patch.featuredQuoteId
  if (quoteId && quoteId !== s.featuredQuoteId) {
    const q = db.quotes.find((x) => x.id === quoteId && x.clientId === clientId)
    if (!q) throw new ApiError(422, "featuredQuoteId is not one of this client's quotes")
    if (q.status !== 'approved') throw new ApiError(422, 'featuredQuoteId is not an approved quote')
  }
  const facultyId = patch.facultyMemberId
  if (facultyId && facultyId !== s.facultyMemberId) {
    const f = db.faculty.find((x) => x.id === facultyId && x.clientId === clientId)
    if (!f) throw new ApiError(422, "facultyMemberId is not one of this client's faculty members")
    if (!f.active) throw new ApiError(422, 'facultyMemberId is an inactive faculty member')
  }
  const imageId = patch.imageAssetId
  if (imageId && imageId !== s.imageAssetId) {
    const m = db.media.find((x) => x.id === imageId && x.clientId === clientId)
    if (!m) throw new ApiError(422, "imageAssetId is not one of this client's media")
    if (m.kind !== 'image' || m.status !== 'approved' || !m.usable)
      throw new ApiError(422, 'imageAssetId is not an approved, usable image')
  }
  if (patch.name !== undefined) s.name = patch.name
  if (patch.headline !== undefined) s.headline = patch.headline?.trim() || null
  if (patch.description !== undefined) s.description = patch.description?.trim() || null
  if (quoteId !== undefined) s.featuredQuoteId = quoteId
  if (facultyId !== undefined) s.facultyMemberId = facultyId
  if (imageId !== undefined) s.imageAssetId = imageId
  return delay(withDetails(s), 200)
}

export async function moveSegment(clientId: string, segmentId: string, direction: -1 | 1): Promise<Segment[]> {
  const s = findSegment(clientId, segmentId)
  const siblings = db.segments
    .filter((x) => x.clientId === s.clientId)
    .sort((a, b) => a.position - b.position)
  const idx = siblings.indexOf(s)
  const target = siblings[idx + direction]
  if (!target) return delay(siblings, 0)
  const tmp = s.position
  s.position = target.position
  target.position = tmp
  return delay([...siblings].sort((a, b) => a.position - b.position).map(withDetails), 150)
}

/** Reassigns WB programs and programs rather than cascading a delete. reassignTo=null means Unassigned. */
export async function deleteSegment(clientId: string, segmentId: string, reassignTo: string | null): Promise<void> {
  findSegment(clientId, segmentId)
  db.wbPrograms = db.wbPrograms.map((w) => (w.segmentId === segmentId ? { ...w, segmentId: reassignTo } : w))
  db.programs.forEach((p) => {
    if (p.segmentId === segmentId) p.segmentId = reassignTo
  })
  db.segments = db.segments.filter((s) => s.id !== segmentId)
  return delay(undefined, 300)
}

/* ------------------------------------------------------- faculty (PC-24) */

export async function listFaculty(clientId: string): Promise<FacultyMember[]> {
  return delay(
    db.faculty.filter((f) => f.clientId === clientId).sort((a, b) => a.name.localeCompare(b.name)),
  )
}

/** As the API: an email names one faculty member per client, ignoring case. */
function assertEmailFree(clientId: string, email: string | null | undefined, self?: string) {
  const key = email?.trim().toLowerCase()
  if (
    key &&
    db.faculty.some(
      (f) => f.clientId === clientId && f.id !== self && f.email?.toLowerCase() === key,
    )
  ) {
    throw new ApiError(409, `A faculty member with ${email} already exists`)
  }
}

function findFaculty(clientId: string, facultyMemberId: string): FacultyMember {
  const f = db.faculty.find((x) => x.id === facultyMemberId && x.clientId === clientId)
  if (!f) throw new ApiError(404, 'Faculty member not found')
  return f
}

export async function createFacultyMember(
  clientId: string,
  input: FacultyMemberInput,
): Promise<FacultyMember> {
  assertEmailFree(clientId, input.email)
  const now = new Date().toISOString()
  const f: FacultyMember = {
    id: uid('fm'),
    clientId,
    name: input.name?.trim() ?? '',
    email: input.email?.trim() || null,
    imageUrl: input.imageUrl?.trim() || null,
    active: input.active ?? true,
    createdAt: now,
    updatedAt: now,
  }
  db.faculty.push(f)
  return delay(f, 200)
}

export async function updateFacultyMember(
  clientId: string,
  facultyMemberId: string,
  patch: FacultyMemberInput,
): Promise<FacultyMember> {
  const f = findFaculty(clientId, facultyMemberId)
  assertEmailFree(clientId, patch.email, facultyMemberId)
  if (patch.name !== undefined) f.name = patch.name.trim()
  if (patch.email !== undefined) f.email = patch.email?.trim() || null
  if (patch.imageUrl !== undefined) f.imageUrl = patch.imageUrl?.trim() || null
  if (patch.active !== undefined) f.active = patch.active
  f.updatedAt = new Date().toISOString()
  return delay(f, 200)
}

/* ------------------------------------------- program content (PC-5, PC-9) */

/** A content record as the mock stores it: the picks by id, the shown quote and image derived. */
interface StoredContent extends Omit<Content, 'featuredQuote' | 'image'> {
  clientId: string
  owner: ContentOwner
  ownerId: string
}

const REQUIRED_CONTENT = ['headline', 'academicInfo', 'outcomesPreface', 'outcomes', 'featuredQuote', 'image'] as const

function storedContent(owner: ContentOwner, ownerId: string): StoredContent | undefined {
  return db.content.find((c) => c.owner === owner && c.ownerId === ownerId)
}

/** As the API shows it: the quote and override image only while approved (ADR-0018 § 8). */
function shownContent(c: StoredContent): Content {
  const { clientId: _client, owner: _owner, ownerId: _ownerId, ...rest } = c
  const q = db.quotes.find((x) => x.id === c.featuredQuoteId && x.status === 'approved')
  const m = db.media.find((x) => x.id === c.imageAssetId && x.status === 'approved' && x.usable)
  const featuredQuote = q
    ? {
        id: q.id,
        text: q.text,
        studentName: q.speakerName,
        classYear: q.classYear,
        majors: q.majors,
        studentImageUrl: q.studentImageUrl ?? null,
      }
    : null
  const image = m
    ? { url: m.url, source: 'override' as const }
    : featuredQuote?.studentImageUrl
      ? { url: featuredQuote.studentImageUrl, source: 'quote' as const }
      : null
  return { ...rest, featuredQuote, image }
}

function missingContent(c: Content | null): string[] {
  if (!c) return [...REQUIRED_CONTENT]
  return REQUIRED_CONTENT.filter((f) => (f === 'outcomes' ? c.outcomes.length === 0 : !c[f]))
}

/** The owner, or a 404; a slate program's WB program, which it inherits from. */
function contentOwnerOf(clientId: string, owner: ContentOwner, ownerId: string): { wbProgramId: string | null } {
  if (owner === 'wb_program') {
    findWbProgram(clientId, ownerId)
    return { wbProgramId: null }
  }
  const slate = db.slatePrograms.find((s) => s.clientId === clientId && s.id === ownerId)
  if (!slate) throw new ApiError(404, 'slate program not found')
  return { wbProgramId: slate.wbProgramId }
}

function readContent(clientId: string, owner: ContentOwner, ownerId: string): ProgramContent {
  const { wbProgramId } = contentOwnerOf(clientId, owner, ownerId)
  const own = storedContent(owner, ownerId)
  const wb = wbProgramId ? db.wbPrograms.find((w) => w.id === wbProgramId) : undefined
  const record = own ?? (wb ? storedContent('wb_program', wb.id) : undefined)
  const content = record ? shownContent(record) : null
  return {
    owner,
    ownerId,
    own: own !== undefined,
    inheritsFrom: wb ? { id: wb.id, name: wb.name } : null,
    content,
    status: content?.status ?? 'not_started',
    missing: missingContent(content),
  }
}

function newContent(clientId: string, owner: ContentOwner, ownerId: string): StoredContent {
  const now = new Date().toISOString()
  const c: StoredContent = {
    id: uid('pc'),
    clientId,
    owner,
    ownerId,
    headline: null,
    academicInfo: null,
    studentFacingValue: null,
    outcomesPreface: null,
    outcomes: [],
    featuredQuoteId: null,
    imageAssetId: null,
    status: 'not_started',
    readyAt: null,
    readyBy: null,
    createdAt: now,
    updatedAt: now,
  }
  db.content = [...db.content, c]
  return c
}

/** Replaced, not edited in place, as `updatePrograms` explains. */
function putContent(c: StoredContent) {
  db.content = db.content.map((x) => (x.id === c.id ? c : x))
}

export async function getProgramContent(clientId: string, owner: ContentOwner, ownerId: string): Promise<ProgramContent> {
  return delay(readContent(clientId, owner, ownerId))
}

export async function updateProgramContent(
  clientId: string,
  owner: ContentOwner,
  ownerId: string,
  patch: ContentPatch,
): Promise<ProgramContent> {
  contentOwnerOf(clientId, owner, ownerId)
  let current = storedContent(owner, ownerId)
  if (!current) {
    if (owner === 'slate_program') {
      throw new ApiError(422, "this slate program shows its WB program's content; create its own first")
    }
    current = newContent(clientId, owner, ownerId)
  }
  if (patch.featuredQuoteId && patch.featuredQuoteId !== current.featuredQuoteId) {
    const q = db.quotes.find((x) => x.clientId === clientId && x.id === patch.featuredQuoteId)
    if (!q) throw new ApiError(422, "featuredQuoteId is not one of this client's quotes")
    if (q.status !== 'approved') throw new ApiError(422, 'featuredQuoteId is not an approved quote')
  }
  if (patch.imageAssetId && patch.imageAssetId !== current.imageAssetId) {
    const m = db.media.find((x) => x.clientId === clientId && x.id === patch.imageAssetId)
    if (!m) throw new ApiError(422, "imageAssetId is not one of this client's media")
    if (m.kind !== 'image' || m.status !== 'approved' || !m.usable) {
      throw new ApiError(422, 'imageAssetId is not an approved, usable image')
    }
  }
  const next: StoredContent = { ...current, updatedAt: new Date().toISOString() }
  for (const key of ['headline', 'academicInfo', 'studentFacingValue', 'outcomesPreface'] as const) {
    const value = patch[key]
    if (value !== undefined) next[key] = value?.trim() || null
  }
  if (patch.outcomes) next.outcomes = patch.outcomes.map((o) => o.trim()).filter(Boolean)
  if (patch.featuredQuoteId !== undefined) next.featuredQuoteId = patch.featuredQuoteId
  if (patch.imageAssetId !== undefined) next.imageAssetId = patch.imageAssetId
  // As the API: the first content moves it to review, and clearing it all moves it back.
  const empty =
    !next.headline &&
    !next.academicInfo &&
    !next.studentFacingValue &&
    !next.outcomesPreface &&
    next.outcomes.length === 0 &&
    !next.featuredQuoteId &&
    !next.imageAssetId
  if (next.status === 'not_started' && !empty) next.status = 'in_review'
  else if (next.status === 'in_review' && empty) next.status = 'not_started'
  putContent(next)
  return delay(readContent(clientId, owner, ownerId), 220)
}

export async function setProgramContentStatus(
  clientId: string,
  owner: ContentOwner,
  ownerId: string,
  status: 'ready' | 'in_review',
  override: boolean,
): Promise<ProgramContent> {
  contentOwnerOf(clientId, owner, ownerId)
  let current = storedContent(owner, ownerId)
  if (!current) {
    if (owner === 'slate_program') {
      throw new ApiError(422, "this slate program shows its WB program's content; approve it there")
    }
    current = newContent(clientId, owner, ownerId)
  }
  const missing = missingContent(shownContent(current))
  if (status === 'ready' && missing.length > 0 && !override) {
    throw new ApiError(409, `required content is missing: ${missing.join(', ')}`)
  }
  const now = new Date().toISOString()
  putContent({
    ...current,
    status,
    readyAt: status === 'ready' ? now : null,
    readyBy: status === 'ready' ? CURRENT_USER.email : null,
    updatedAt: now,
  })
  return delay(readContent(clientId, owner, ownerId), 200)
}

export async function createSlateContent(
  clientId: string,
  slateProgramId: string,
  copyFromWbProgram: boolean,
): Promise<ProgramContent> {
  const { wbProgramId } = contentOwnerOf(clientId, 'slate_program', slateProgramId)
  if (storedContent('slate_program', slateProgramId)) throw new ApiError(409, 'this slate program has its own content')
  const source = wbProgramId ? storedContent('wb_program', wbProgramId) : undefined
  if (copyFromWbProgram && !source) throw new ApiError(422, "this slate program's WB program has no content to copy")
  const created = newContent(clientId, 'slate_program', slateProgramId)
  if (copyFromWbProgram && source) {
    putContent({
      ...created,
      headline: source.headline,
      academicInfo: source.academicInfo,
      studentFacingValue: source.studentFacingValue,
      outcomesPreface: source.outcomesPreface,
      outcomes: [...source.outcomes],
      featuredQuoteId: source.featuredQuoteId,
      imageAssetId: source.imageAssetId,
      status: 'in_review',
    })
  }
  return delay(readContent(clientId, 'slate_program', slateProgramId), 220)
}

export async function deleteSlateContent(clientId: string, slateProgramId: string): Promise<void> {
  contentOwnerOf(clientId, 'slate_program', slateProgramId)
  const own = storedContent('slate_program', slateProgramId)
  if (!own) throw new ApiError(404, 'this slate program has no content of its own')
  db.content = db.content.filter((c) => c.id !== own.id)
  return delay(undefined, 200)
}

export async function listContentSummaries(clientId: string): Promise<ContentSummaries> {
  return delay({
    wbPrograms: db.wbPrograms
      .filter((w) => w.clientId === clientId)
      .map((w) => {
        const read = readContent(clientId, 'wb_program', w.id)
        return { id: w.id, status: read.status, missing: read.missing }
      }),
    slatePrograms: db.slatePrograms
      .filter((s) => s.clientId === clientId)
      .map((s) => {
        const read = readContent(clientId, 'slate_program', s.id)
        return { id: s.id, status: read.status, missing: read.missing, own: read.own }
      }),
  })
}

/* ---------------------------------------------------- WB programs (PC-33) */

/** As the API compares names: case, punctuation, spacing and &/and do not count. */
const wbNameKey = (name: string) =>
  name.toLowerCase().replace(/&/g, ' and ').replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean).join(' ')

function assertWbNameFree(clientId: string, name: string, self?: string) {
  const key = wbNameKey(name)
  if (db.wbPrograms.some((w) => w.clientId === clientId && w.id !== self && wbNameKey(w.name) === key)) {
    throw new ApiError(409, `A WB program named ${name} already exists`)
  }
}

function findWbProgram(clientId: string, wbProgramId: string): seed.WbProgramSeed {
  const w = db.wbPrograms.find((x) => x.id === wbProgramId && x.clientId === clientId)
  if (!w) throw new ApiError(404, 'WB program not found')
  return w
}

function withLinks({ slateProgramId, ...w }: seed.WbProgramSeed): WbProgram {
  const own = db.slatePrograms.find((s) => s.id === slateProgramId)
  return {
    ...w,
    slateProgram: own ? { id: own.id, slateName: own.slateName, enabled: own.enabled } : null,
    slatePrograms: db.slatePrograms
      .filter((s) => s.wbProgramId === w.id)
      .sort((a, b) => a.slateName.localeCompare(b.slateName))
      .map((s) => ({ id: s.id, slateName: s.slateName, enabled: s.enabled })),
    programCount: db.programs.filter((p) => p.wbProgramId === w.id && p.mergedInto === null).length,
  }
}

/**
 * Replaces the matching rows rather than editing them in place: react-query
 * keeps the old list when the new one is deep-equal to it, and an in-place
 * edit makes the old one equal too.
 */
function updatePrograms(match: (p: Program) => boolean, patch: (p: Program) => Partial<Program>) {
  db.programs = db.programs.map((p) => (match(p) ? { ...p, ...patch(p) } : p))
}

/** The crawled rows' copies of the WB program's name and segment, as the API keeps them. */
function syncRows(w: seed.WbProgramSeed) {
  updatePrograms((p) => p.wbProgramId === w.id, () => ({ wbProgram: w.name, segmentId: w.segmentId }))
}

export async function listWbPrograms(clientId: string): Promise<WbProgram[]> {
  return delay(
    db.wbPrograms
      .filter((w) => w.clientId === clientId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(withLinks),
  )
}

export async function createWbProgram(
  clientId: string,
  input: { name: string; segmentId?: string | null },
): Promise<WbProgram> {
  const name = input.name.trim()
  assertWbNameFree(clientId, name)
  if (input.segmentId) findSegment(clientId, input.segmentId)
  const now = new Date().toISOString()
  const w: seed.WbProgramSeed = {
    id: uid('wb'), clientId, name, segmentId: input.segmentId ?? null, slateProgramId: null, createdAt: now, updatedAt: now,
  }
  db.wbPrograms.push(w)
  return delay(withLinks(w), 200)
}

export async function updateWbProgram(clientId: string, wbProgramId: string, patch: WbProgramPatch): Promise<WbProgram> {
  const current = findWbProgram(clientId, wbProgramId)
  if (patch.name !== undefined) assertWbNameFree(clientId, patch.name, wbProgramId)
  if (patch.segmentId) findSegment(clientId, patch.segmentId)
  if (patch.slateProgramId && !db.slatePrograms.some((s) => s.clientId === clientId && s.id === patch.slateProgramId)) {
    throw new ApiError(422, "slateProgramId is not one of this client's slate programs")
  }
  const w: seed.WbProgramSeed = {
    ...current,
    name: patch.name !== undefined ? patch.name.trim() : current.name,
    segmentId: patch.segmentId !== undefined ? patch.segmentId : current.segmentId,
    slateProgramId: patch.slateProgramId !== undefined ? patch.slateProgramId : current.slateProgramId,
    updatedAt: new Date().toISOString(),
  }
  db.wbPrograms = db.wbPrograms.map((x) => (x.id === w.id ? w : x))
  syncRows(w)
  return delay(withLinks(w), 200)
}

/** Its crawled programs become unassigned, its slate programs unmapped. */
export async function deleteWbProgram(clientId: string, wbProgramId: string): Promise<void> {
  findWbProgram(clientId, wbProgramId)
  updatePrograms((p) => p.wbProgramId === wbProgramId, () => ({ wbProgramId: null, wbProgram: null }))
  db.slatePrograms = db.slatePrograms.map((s) => (s.wbProgramId === wbProgramId ? { ...s, wbProgramId: null } : s))
  db.wbPrograms = db.wbPrograms.filter((w) => w.id !== wbProgramId)
  // Its content goes with it, as migrations/032's ON DELETE CASCADE.
  db.content = db.content.filter((c) => !(c.owner === 'wb_program' && c.ownerId === wbProgramId))
  return delay(undefined, 200)
}

/** One segment, or none, on many WB programs. All or nothing. */
export async function setWbProgramSegment(clientId: string, ids: string[], segmentId: string | null): Promise<void> {
  const chosen = new Set(ids.map((id) => findWbProgram(clientId, id).id))
  if (segmentId) findSegment(clientId, segmentId)
  db.wbPrograms = db.wbPrograms.map((w) => (chosen.has(w.id) ? { ...w, segmentId } : w))
  db.wbPrograms.filter((w) => chosen.has(w.id)).forEach(syncRows)
  return delay(undefined, 180)
}

/** Crawled programs under a WB program (by id, or a name found or created), or under none. */
export async function assignPrograms(clientId: string, ids: string[], target: WbProgramTarget): Promise<void> {
  const rows = ids.map((id) => {
    const p = db.programs.find((x) => x.clientId === clientId && x.id === id)
    if (!p) throw new ApiError(404, `program not found: ${id}`)
    return p
  })
  if (target && rows.some((p) => p.level === 'graduate')) {
    throw new ApiError(422, 'a graduate program cannot be put under a WB program')
  }
  let wb: seed.WbProgramSeed | null = null
  if (target && 'wbProgramId' in target) {
    wb = db.wbPrograms.find((w) => w.clientId === clientId && w.id === target.wbProgramId) ?? null
    if (!wb) throw new ApiError(422, "wbProgramId is not one of this client's WB programs")
  } else if (target) {
    const key = wbNameKey(target.name)
    wb = db.wbPrograms.find((w) => w.clientId === clientId && wbNameKey(w.name) === key) ?? null
    if (!wb) {
      const created = await createWbProgram(clientId, { name: target.name })
      wb = findWbProgram(clientId, created.id)
    }
  }
  const chosen = new Set(rows.map((p) => p.id))
  updatePrograms((p) => chosen.has(p.id), () => ({ wbProgramId: wb?.id ?? null, wbProgram: wb?.name ?? null }))
  if (wb) syncRows(wb)
  return delay(undefined, 180)
}

/* -------------------------------------------- reviewable entity registry */

/** Review operations only touch Reviewable fields, so one typed registry serves
 *  all four entity types without per-entity branching. */
const tables: Record<EntityType, () => Reviewable[]> = {
  programs: () => db.programs,
  quotes: () => db.quotes,
  media: () => db.media,
  facts: () => db.facts,
}

export async function listPrograms(clientId: string): Promise<Program[]> {
  return delay(db.programs.filter((p) => p.clientId === clientId))
}

function programRef(p: Program): ProgramRef {
  return { id: p.id, name: p.name, wbProgram: p.wbProgram ?? p.name, degree: p.degree }
}

export async function getProgram(clientId: string, programId: string): Promise<ProgramDetail> {
  const program = db.programs.find((p) => p.clientId === clientId && p.id === programId)
  if (!program) throw new ApiError(404, 'program not found')
  const merged = db.programs.filter((p) => p.mergedInto?.id === programId).map(programRef)
  return delay({ ...program, merged })
}

export async function listSlatePrograms(clientId: string): Promise<SlateProgram[]> {
  const programs = db.programs.filter((p) => p.clientId === clientId)
  return delay(
    db.slatePrograms
      .filter((s) => s.clientId === clientId)
      .sort((a, b) => a.slateName.localeCompare(b.slateName))
      .map(({ programId, wbProgramId, ...s }) => {
        const representative = programs.find((p) => p.id === programId)
        const wb = db.wbPrograms.find((w) => w.id === wbProgramId)
        return {
          ...s,
          wbProgram: wb ? { id: wb.id, name: wb.name } : null,
          program: representative ? programRef(representative) : null,
          mappedPrograms: programs.filter((p) => p.slateProgram?.id === s.id).map(programRef),
        }
      }),
  )
}
export async function listQuotes(clientId: string): Promise<Quote[]> {
  return delay(db.quotes.filter((q) => q.clientId === clientId))
}

export async function getQuote(clientId: string, quoteId: string): Promise<QuoteDetail> {
  const quote = db.quotes.find((q) => q.clientId === clientId && q.id === quoteId)
  if (!quote) throw new ApiError(404, 'quote not found')
  return delay(quote)
}
export async function listMedia(clientId: string): Promise<MediaItem[]> {
  return delay(db.media.filter((m) => m.clientId === clientId))
}

export async function getMedia(clientId: string, assetId: string): Promise<MediaDetail> {
  const asset = db.media.find((m) => m.clientId === clientId && m.id === assetId)
  if (!asset) throw new ApiError(404, 'media asset not found')
  return delay(asset)
}
export async function listFacts(clientId: string): Promise<PartnerFact[]> {
  return delay(db.facts.filter((f) => f.clientId === clientId))
}

/** One client's rows: the API scopes every write by the client in its path. */
function clientRows(clientId: string, entity: EntityType): Reviewable[] {
  return tables[entity]().filter((r) => r.clientId === clientId)
}

export async function setStatus(
  clientId: string,
  entity: EntityType,
  ids: string[],
  status: ReviewStatus,
): Promise<void> {
  const rows = clientRows(clientId, entity)
  ids.forEach((id) => {
    const row = rows.find((r) => r.id === id)
    if (!row) return
    row.status = status
    row.verdicts = [
      ...row.verdicts,
      { at: new Date().toISOString(), by: CURRENT_USER.email, verdict: status, entityHash: row.entityHash },
    ]
    row.previouslyRejected = false
  })
  return delay(undefined, 220)
}

export async function setMark(
  clientId: string,
  entity: EntityType,
  id: string,
  kind: MarkKind,
  on: boolean,
): Promise<void> {
  const row = clientRows(clientId, entity).find((r) => r.id === id)
  if (row) row[kind] = on
  return delay(undefined, 120)
}

export async function setTags(clientId: string, entity: EntityType, ids: string[], tags: string[]): Promise<void> {
  ids.forEach((id) => {
    const row = clientRows(clientId, entity).find((r) => r.id === id)
    if (row) row.tags = tags
  })
  return delay(undefined, 150)
}

export async function addTagToMany(clientId: string, entity: EntityType, ids: string[], tag: string): Promise<void> {
  ids.forEach((id) => {
    const row = clientRows(clientId, entity).find((r) => r.id === id)
    if (row && !row.tags.includes(tag)) row.tags = [...row.tags, tag]
  })
  return delay(undefined, 180)
}

export async function deleteEntities(entity: EntityType, ids: string[]): Promise<void> {
  const rows = tables[entity]()
  ids.forEach((id) => {
    const i = rows.findIndex((r) => r.id === id)
    if (i !== -1) rows.splice(i, 1)
  })
  return delay(undefined, 250)
}

/* -------------------------------------------------------- entity updates */

export async function upsertProgram(clientId: string, patch: ProgramPatch): Promise<Program> {
  if (patch.id) {
    const i = db.programs.findIndex((p) => p.id === patch.id)
    if (i === -1) throw new ApiError(404, 'Program not found')
    const current = db.programs[i]!
    // As the API: only the page, tags and level are a program's own edits (PC-33, PC-25). Blank clears the page.
    const programUrl = patch.programUrl === undefined ? current.programUrl : patch.programUrl?.trim() || null
    const level = patch.level ?? current.level
    // A graduate program leaves its WB program (ADR-0022).
    const unassign = patch.level === 'graduate' ? { wbProgramId: null, wbProgram: null } : {}
    db.programs[i] = { ...current, programUrl, tags: patch.tags ?? current.tags, level, ...unassign }
    return delay(db.programs[i]!, 280)
  }
  const wb = patch.wbProgramId ? findWbProgram(clientId, patch.wbProgramId) : null
  const name = patch.name ?? 'Untitled program'
  const id = uid('pg')
  const created: ProgramDetail = {
    id,
    clientId,
    name,
    wbProgramId: wb?.id ?? null,
    wbProgram: wb?.name ?? null,
    degree: patch.degree ?? null,
    level: patch.level ?? null,
    department: null,
    description: patch.description ?? null,
    segmentId: wb?.segmentId ?? null,
    programTypeId: null,
    programUrl: patch.programUrl?.trim() || null,
    sourceUrl: null,
    slateProgram: null,
    mergedInto: null,
    status: 'approved',
    favorite: false,
    flagged: false,
    tags: patch.tags ?? [],
    entityHash: hash(),
    verdicts: [
      { at: new Date().toISOString(), by: CURRENT_USER.email, verdict: 'approved', entityHash: hash() },
    ],
    previouslyRejected: false,
    source: 'manual',
    notes: null,
    credentialLength: null,
    cipCode: null,
    deliveryModes: [],
    concentrations: [],
    pages: [],
    merged: [],
  }
  db.programs.push(created)
  return delay(created, 280)
}

/**
 * `PATCH /slate-programs/{id}` (PC-33): map it to a WB program, or unmap it. A
 * WB program with no slate program of its own takes one, as the API does (PC-37).
 */
export async function mapSlateProgram(
  clientId: string,
  slateProgramId: string,
  wbProgramId: string | null,
): Promise<SlateProgram> {
  const slate = db.slatePrograms.find((s) => s.clientId === clientId && s.id === slateProgramId)
  if (!slate) throw new ApiError(404, 'slate program not found')
  if (wbProgramId !== null && !db.wbPrograms.some((w) => w.clientId === clientId && w.id === wbProgramId)) {
    throw new ApiError(422, "wbProgramId is not one of this client's WB programs")
  }
  db.slatePrograms = db.slatePrograms.map((s) => (s.id === slate.id ? { ...s, wbProgramId } : s))
  db.wbPrograms = db.wbPrograms.map((w) =>
    w.id === wbProgramId && w.slateProgramId === null ? { ...w, slateProgramId: seed.pickSlateProgram(w, db.slatePrograms) } : w,
  )
  const [row] = (await listSlatePrograms(clientId)).filter((s) => s.id === slateProgramId)
  return row!
}

export async function upsertQuote(
  clientId: string,
  patch: Partial<Quote> & { id?: string },
): Promise<QuoteDetail> {
  if (patch.id) {
    const i = db.quotes.findIndex((q) => q.id === patch.id)
    if (i === -1) throw new ApiError(404, 'Quote not found')
    db.quotes[i] = { ...db.quotes[i]!, ...patch }
    return delay(db.quotes[i]!, 280)
  }
  const created: QuoteDetail = {
    id: uid('qt'),
    clientId,
    text: patch.text ?? '',
    speakerName: patch.speakerName ?? null,
    classYear: patch.classYear ?? null,
    majors: patch.majors ?? null,
    currentPosition: patch.currentPosition ?? null,
    hometown: patch.hometown ?? null,
    context: null,
    quoteDate: null,
    dateProvenance: null,
    sourceUrl: null,
    studentImageUrl: null,
    majorMinorMentioned: null,
    notes: null,
    substance: null,
    programs: [],
    status: 'approved',
    favorite: false,
    flagged: false,
    tags: patch.tags ?? [],
    entityHash: hash(),
    verdicts: [
      { at: new Date().toISOString(), by: CURRENT_USER.email, verdict: 'approved', entityHash: hash() },
    ],
    previouslyRejected: false,
    source: 'manual',
  }
  db.quotes.push(created)
  return delay(created, 280)
}

export async function upsertMedia(
  clientId: string,
  patch: Partial<MediaItem> & { id?: string },
): Promise<MediaDetail> {
  if (patch.id) {
    const i = db.media.findIndex((m) => m.id === patch.id)
    if (i === -1) throw new ApiError(404, 'Media not found')
    db.media[i] = { ...db.media[i]!, ...patch }
    return delay(db.media[i]!, 280)
  }
  // An upload. Mock only: the API has no create until storage exists (APP-138).
  const created: MediaDetail = {
    id: uid('md'),
    clientId,
    kind: patch.kind ?? 'image',
    url: patch.url ?? '',
    youtubeId: null,
    title: patch.title ?? null,
    description: patch.description ?? null,
    width: null,
    height: null,
    usable: true,
    sourceUrl: null,
    programs: [],
    status: 'approved',
    favorite: false,
    flagged: false,
    tags: patch.tags ?? [],
    entityHash: hash(),
    verdicts: [
      { at: new Date().toISOString(), by: CURRENT_USER.email, verdict: 'approved', entityHash: hash() },
    ],
    previouslyRejected: false,
    source: 'manual',
  }
  db.media.push(created)
  return delay(created, 400)
}

export async function upsertFact(
  clientId: string,
  patch: Partial<PartnerFact> & { id?: string },
): Promise<PartnerFact> {
  if (patch.id) {
    const i = db.facts.findIndex((f) => f.id === patch.id)
    if (i === -1) throw new ApiError(404, 'Fact not found')
    db.facts[i] = { ...db.facts[i]!, ...patch }
    return delay(db.facts[i]!, 260)
  }
  const created: PartnerFact = {
    id: uid('ft'),
    clientId,
    kind: patch.kind ?? 'additional',
    label: patch.label ?? '',
    value: patch.value ?? '',
    sourceUrl: patch.sourceUrl ?? null,
    status: 'approved',
    favorite: false,
    flagged: false,
    tags: patch.tags ?? [],
    entityHash: hash(),
    verdicts: [
      { at: new Date().toISOString(), by: CURRENT_USER.email, verdict: 'approved', entityHash: hash() },
    ],
    previouslyRejected: false,
    source: 'manual',
  }
  db.facts.push(created)
  return delay(created, 260)
}

/* ---------------------------------------------------------- copy library */

export async function listCopyDocs(clientId: string): Promise<CopyDoc[]> {
  return delay(db.copyDocs.filter((c) => c.clientId === clientId))
}

export async function upsertCopyDoc(
  clientId: string,
  patch: Partial<CopyDoc> & { id?: string },
): Promise<CopyDoc> {
  const nowIso = new Date().toISOString()
  if (patch.id) {
    const i = db.copyDocs.findIndex((c) => c.id === patch.id)
    if (i === -1) throw new ApiError(404, 'Copy not found')
    const prev = db.copyDocs[i]!
    const bodyChanged = patch.body !== undefined && patch.body !== prev.body
    const version = bodyChanged ? prev.version + 1 : prev.version
    const next: CopyDoc = {
      ...prev,
      ...patch,
      version,
      updatedAt: nowIso,
      history: bodyChanged
        ? [
            ...prev.history,
            { version, body: patch.body!, savedAt: nowIso, author: CURRENT_USER.name },
          ]
        : prev.history,
    }
    db.copyDocs[i] = next
    return delay(next, 300)
  }
  const created: CopyDoc = {
    id: uid('cp'),
    clientId,
    name: patch.name ?? 'Untitled',
    copyType: patch.copyType ?? 'other',
    body: patch.body ?? '',
    tags: patch.tags ?? [],
    favorite: false,
    author: CURRENT_USER.name,
    createdAt: nowIso,
    updatedAt: nowIso,
    version: 1,
    origin: 'manual',
    history: [{ version: 1, body: patch.body ?? '', savedAt: nowIso, author: CURRENT_USER.name }],
  }
  db.copyDocs.push(created)
  return delay(created, 300)
}

export async function setCopyFavorite(id: string, favorite: boolean): Promise<void> {
  const doc = db.copyDocs.find((c) => c.id === id)
  if (doc) doc.favorite = favorite
  return delay(undefined, 120)
}

export async function deleteCopyDoc(id: string): Promise<void> {
  db.copyDocs = db.copyDocs.filter((c) => c.id !== id)
  return delay(undefined, 220)
}
